import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ArrowLeftRight, Landmark, Shield, Zap, Gift, X, ChevronDown, Sparkles } from 'lucide-react';
import AddTransactionDialog from './AddTransactionDialog';

const QuickActions = ({ onActionComplete }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [txDefaultType, setTxDefaultType] = useState('buy_primary');

  const openTx = (defaultType) => {
    setTxDefaultType(defaultType);
    setShowTransactionDialog(true);
    setIsOpen(false);
  };

  const actions = [
    {
      id: 'tx',
      label: 'Buy / Sell',
      sub: 'Record a stock trade',
      icon: ArrowLeftRight,
      color: 'text-emerald-600 bg-emerald-50',
      onClick: () => openTx('buy_primary')
    },
    {
      id: 'bulk-ai',
      label: 'Bulk / AI Parser',
      sub: 'Paste broker updates or CSV',
      icon: Sparkles,
      color: 'text-indigo-600 bg-indigo-50',
      onClick: () => { navigate('/bulk-import'); setIsOpen(false); }
    },
    {
      id: 'pledge',
      label: 'Pledge / Free',
      sub: 'Pledge or release shares',
      icon: Shield,
      color: 'text-amber-600 bg-amber-50',
      onClick: () => openTx('las_pledge')
    },
    {
      id: 'loan-update',
      label: 'Loan Update',
      sub: 'Daily MTF / LAS status',
      icon: Zap,
      color: 'text-indigo-600 bg-indigo-50',
      onClick: () => { navigate('/debt-management?tab=daily-update'); setIsOpen(false); }
    },
    {
      id: 'banks',
      label: 'Banks',
      sub: 'Balance / OD updates',
      icon: Landmark,
      color: 'text-blue-600 bg-blue-50',
      onClick: () => { navigate('/banks'); setIsOpen(false); }
    },
    {
      id: 'dividends',
      label: 'Dividends',
      sub: 'Bulk dividend entry',
      icon: Gift,
      color: 'text-rose-600 bg-rose-50',
      onClick: () => { navigate('/bulk-dividends'); setIsOpen(false); }
    },
  ];

  return (
    <>
      {/* Top-right Quick Action button */}
      <div className="fixed top-3 right-3 sm:top-4 sm:right-4 z-40" data-testid="quick-action-fab">
        {/* Pill button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full shadow-md border border-emerald-200 font-medium text-sm transition-all ${
            isOpen
              ? 'bg-gray-700 text-white border-gray-700'
              : 'bg-white text-emerald-700 hover:bg-emerald-50 hover:shadow-lg'
          }`}
          data-testid="quick-action-toggle"
          aria-label="Quick actions"
        >
          {isOpen ? <X size={14} /> : <Plus size={14} />}
          <span className="hidden sm:inline">Quick Action</span>
          {!isOpen && <ChevronDown size={12} className="opacity-70" />}
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in-0 slide-in-from-top-2 duration-150"
            data-testid="quick-action-menu"
          >
            <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wider border-b bg-gray-50">
              Quick actions
            </div>
            {actions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={action.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors text-left border-b last:border-b-0"
                  data-testid={`quick-action-${action.id}`}
                >
                  <div className={`p-1.5 rounded-lg ${action.color}`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 leading-tight">{action.label}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{action.sub}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/10 z-30"
          onClick={() => setIsOpen(false)}
          data-testid="quick-action-backdrop"
        />
      )}

      {/* Transaction Dialog */}
      {showTransactionDialog && (
        <AddTransactionDialog
          open={showTransactionDialog}
          isOpen={showTransactionDialog}
          defaultTransactionType={txDefaultType}
          onSuccess={() => {
            setShowTransactionDialog(false);
            if (onActionComplete) onActionComplete();
          }}
          onClose={() => setShowTransactionDialog(false)}
        />
      )}
    </>
  );
};

export default QuickActions;
